/**
 * Module: ReRunAnalysis
 * Description: UI for re-running analysis on selected documents and clauses.
 * - Fetches available documents on mount
 * - Allows selecting multiple documents and clauses
 * - Submits a batch rerun request to the server
 * Notes:
 * - Uses `apiCall` to communicate with backend endpoints.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import { getFilenameFromURL, getUsername } from "../../utils/common_functions";
import { apiCall } from "../../utils/server_utils";
import CommonIcon from '../common/customicon';
import Message from "../common/message";
import { API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE } from "../../utils/config_mapping";
import PageLoader from '../common/pageloader';
import * as ReactDOM from 'react-dom';
import {trackError} from "../../utils/error_tracker"
import HeaderLogo  from "../structure/headerlogo";

/**
 * ReRunAnalysis component
 * Renders the page allowing users to select documents and clauses and trigger a re-run.
 * @component
 */
export const ReRunAnalysis = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]); // API data for documents
  const [clausesData, setClausesData] = useState(["Property", "Channel", "Customer", "Product type", "Category", "Territory", "Gender"]); // Clause options
  const [filterTextDocuments, setFilterTextDocuments] = useState(""); // For document filtering
  const [filterTextClauses, setFilterTextClauses] = useState(""); // For clause filtering
  const [selectedDocument, setSelectedDocument] = useState(null); // Selected document
  const [selectedClauses, setSelectedClauses] = useState([]); // Selected clauses
  const [selectedDocuments, setSelectedDocuments] = useState([]); // Track selected documents

  useEffect(() => {
    // Fetch data from the API
    /**
     * fetchContracts
     * Retrieves contract list from backend and stores in `data` state.
     * Handles loading state and sorts results by `FilePath` for display.
     * @returns {Promise<void>}
     */
    const fetchContracts = async () => {
        setLoading(true); // show loading spinner
      try {
        const result = await apiCall("GET", `${API_SERVER_URL}contracts/`);
        if (result.code === API_SUCCESS_RESPONSE_CODE) {

          const sortedData = [...result.data].sort((a, b) =>
            a.FilePath.localeCompare(b.FilePath)
          );
          setData(sortedData);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error("Error fetching contracts", error);
      }
    };
    fetchContracts();
  }, []);


  /**
   * resetform
   * Clear selection state for documents and clauses and uncheck any checkboxes
   * in the DOM. Mutates DOM checkboxes directly to keep legacy markup in sync.
   */
  const resetform = () => {
    setSelectedClauses([])
    setSelectedDocument(null);
    setSelectedDocuments([])
    var uncheck=document.getElementsByTagName('input');
    for(var i=0;i<uncheck.length;i++)
    {
     if(uncheck[i].type=='checkbox')
     {
      uncheck[i].checked=false;
     }
    }
  }

  // Filter documents based on the search text
  const filteredDocuments = data.filter((doc) =>
    getFilenameFromURL(doc.FilePath).toLowerCase().includes(filterTextDocuments.toLowerCase())
  );

  // Filter clauses based on the search text
  const filteredClauses = clausesData.filter((clause) =>
    clause.toLowerCase().includes(filterTextClauses.toLowerCase())
  );

  /**
   * handleDocumentSelectionBatch
   * Toggle a document in the `selectedDocuments` array for batch processing.
   * @param {Object} doc - Document object to toggle selection for
   */
  const handleDocumentSelectionBatch = (doc) => {
    const isSelected = selectedDocuments.find(selected => selected._id === doc._id);
    if (isSelected) {
      setSelectedDocuments(selectedDocuments.filter(selected => selected._id !== doc._id));
    } else {
      setSelectedDocuments([...selectedDocuments, doc]);
    }
  };

    // **Select All Documents** functionality
    /**
     * handleSelectAllDocuments
     * Select or deselect all currently filtered documents.
     * @param {Event} e - Change event from the checkbox
     */
    const handleSelectAllDocuments = (e) => {
      if (e.target.checked) {
        setSelectedDocuments(filteredDocuments);
      } else {
        setSelectedDocuments([]);
      }
    };
  
    // **Select All Clauses** functionality
    /**
     * handleSelectAllClauses
     * Select or deselect all currently filtered clauses.
     * @param {Event} e - Change event from the checkbox
     */
    const handleSelectAllClauses = (e) => {
      if (e.target.checked) {
        setSelectedClauses(filteredClauses);
      } else {
        setSelectedClauses([]);
      }
    };


  // Handle clause selection
  /**
   * handleClauseSelection
   * Toggle a clause in `selectedClauses`.
   * @param {string} clause - Clause label to toggle
   */
  const handleClauseSelection = (clause) => {
    if (selectedClauses.includes(clause)) {
        setSelectedClauses(selectedClauses.filter((c) => c !== clause));
      } else {
        setSelectedClauses([...selectedClauses, clause]);
      }
  };


  /**
   * startReRun
   * Send a request to rerun analysis for the selected documents and clauses.
   * Builds request payload from `selectedDocuments` and `selectedClauses`.
   * On success, displays a success message and resets the form.
   * @returns {Promise<void>}
   */
  const startReRun = async () => {
    let apiUrl = API_SERVER_URL+ 'rerun/'
    let method = "POST"
    const logged_user_name = getUsername();
    let request_object = {}
    request_object.doc_ids = selectedDocuments.map(item => item.DocumentNo);
    request_object.clauses = selectedClauses
    request_object.last_imported_by = logged_user_name
    try {
        const result = await apiCall(method,apiUrl,request_object);
        const targetElement = document.getElementById("app_err");
        if(result!==undefined){
        if (result.code === API_SUCCESS_RESPONSE_CREATED_CODE) {
            if (targetElement) {
              ReactDOM.unmountComponentAtNode(targetElement);
            }
            ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
            resetform()
        } else {
          if (targetElement) {
            ReactDOM.unmountComponentAtNode(targetElement);
          }
          ReactDOM.render(<Message type='error' message={result.message} />, targetElement);
          trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
        }
        }
    } catch (error) {
      console.error("Error:", error);
    }
    }

  /**
   * reRunClausesByDocument
   * Convenience wrapper to start the rerun process (keeps naming semantic in JSX handlers).
   */
  const reRunClausesByDocument =() => {
    startReRun()
  }
  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
            <div className="flex-grow-1" id="row_heading">
              <div className="page-title">Re-Run Analysis</div>
              <p className="text-muted my-2 ff-secondary">
                Re-analyze documents with the latest intelligence engine
              </p>
            </div>
            <div id="app_err"></div>
          </div>
            {loading ? (
                 <PageLoader />
            ) :  data.length > 0 ?
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      {/* Document Search */}
                      <div className="row g-2 mb-4 mt-1" id="row_search">

                             <div className="col-sm-6">
                              <label>Select Documents</label>
                              </div>
                              <div className="col-sm-6">
                              <label className='highlight-clause float-end'>
                                {
                                selectedDocuments.length > 0 ? selectedDocuments.length > 1 ? selectedDocuments.length+" documents selected": selectedDocuments.length+" document selected" : "" }
                                </label>
                              </div>

                        <div className="col-sm-12">
                          <div className="search-box">
                            <input
                              type="text"
                              className="form-control"
                              id="searchDocuments"
                              placeholder="Search documents..."
                              value={filterTextDocuments}
                              onChange={(e) => setFilterTextDocuments(e.target.value)}
                            />
                            <i className="grid-search">
                              <CommonIcon iconName="Search" size={"14px"} />
                            </i>
                          </div>
                        </div>
                      </div>
                      {/* Document List */}
                      <div className="card">
                      <div className='card-heading'><div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={'SelectAllDocuments'}
                                        onChange={handleSelectAllDocuments}
                                        checked={selectedDocuments.length === filteredDocuments.length && filteredDocuments.length > 0}
            
                                      />
                                      <label className="form-check-label" htmlFor={`SelectAllDocuments`}>
                                      Select All Documents
                                      </label>
                                </div></div>
                        <div className="card-body no-padding">
                          <div style={{ height: "200px", overflow: "auto","margin":"5px" }}>
                          {filteredDocuments.map((doc) => (
                        <div className="document-row" key={doc._id} onClick={() => handleDocumentSelectionBatch(doc)}
                        
                        style={{
                          border: selectedDocuments.includes(doc) ? '0px solid #007bff' : '0px solid #ccc',
                          borderRadius:"10px",
                          padding: '10px',
                          marginBottom: '5px',
                          cursor: 'pointer',
                          backgroundColor: selectedDocuments.includes(doc) ? '#f3f4f5' : '#fff',
                        }}
                        >
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`doc-${doc._id}`}
                              checked={selectedDocuments.some(selected => selected._id === doc._id)}
                              onChange={() => handleDocumentSelectionBatch(doc)}
                            />
                          </div>
                          <div>
                            <CommonIcon iconName="FileText" size={"17px"} color="#6B7280" />
                          </div>
                          <div className="document-info ml-5">
                            <h6>{getFilenameFromURL(doc.FilePath)}</h6>
                            <p>Last processed: {doc.importdate}</p>
                          </div>
                        </div>
                      ))}
                        {filteredDocuments.length===0 ? "No Documents Found" : ""}
                          </div>
                        </div>
                      </div>
                      {/* Clauses Section (shown only when document is selected) */}
                      {selectedDocuments.length > 0 && (
                        <>
                          <div className="row g-2 mb-4 mt-1">
                            <div className="col-sm-6">
                              <label>Select Clauses to Re-Run</label>
                              </div>
                              <div className="col-sm-6">
                              <label className='highlight-clause float-end'>
                                {
                                selectedClauses.length > 0 ? selectedClauses.length > 1 ? selectedClauses.length+" clauses selected": selectedClauses.length+" clause selected" : "" }
                                </label>
                              </div>
                              <div className="search-box">
                                <input
                                  type="text"
                                  className="form-control"
                                  id="searchClauses"
                                  placeholder="Search clauses..."
                                  value={filterTextClauses}
                                  onChange={(e) => setFilterTextClauses(e.target.value)}
                                />
                                <i className="grid-search">
                                  <CommonIcon iconName="Search" size={"14px"} />
                                </i>
                             
                            </div>
                          </div>
                          {/* Clauses List */}
                          <div className="card">
                            <div className="card-body no-padding">
                              <div className='card-heading'>
                                
                              <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={'SelectAllClauses'}
                                        onChange={handleSelectAllClauses}
                                        checked={selectedClauses.length === filteredClauses.length && filteredClauses.length > 0}
                                      />
                                      <label className="form-check-label" htmlFor={`SelectAllClauses`}>
                                      Select All Clauses
                                      </label>
                                </div></div>
                              <div style={{ height: "200px", overflow: "auto","margin":"5px"  }}>
                                {filteredClauses.map((clause, index) => (
                                  <div  key={index}
                                  style={{
                                    border: selectedClauses.includes(clause) ? '0px solid #007bff' : '0px solid #ccc',
                                    borderRadius:"10px",
                                    padding: '10px',
                                    marginBottom: '5px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedClauses.includes(clause) ? '#f3f4f5' : '#fff',
                                  }}
                                  >
                                    <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`flexCheckDefault_${index}`}
                                      // Check if the clause is in the selectedClauses array
                                      checked={selectedClauses.includes(clause)}
                                      // Toggle clause selection
                                      onChange={() => handleClauseSelection(clause)}
                                    />
                                    <label className="form-check-label" htmlFor={`flexCheckDefault_${index}`}>
                                      {clause}
                                    </label>
                                  </div>

                                  </div>
                                ))}
                                {filteredClauses.length===0 ? "No Clauses Found" : ""}
                              </div>
                             
                            </div>
                          </div>
                        </>
                      )}

                      {/* Re-Run All Clauses Button (enabled only when document is selected) */}
                      <div className="row">
                       
                        <div className="col-lg-12">
                          <button
                            className="btn btn-primary float-end"
                            onClick={reRunClausesByDocument}
                            disabled={!selectedClauses.length}  // Disabled if no document or clauses are selected
                            >
                            <CommonIcon iconName="RefreshCw" size={"14px"} color="#fff" /> Re-Run Analysis
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div> : ""}
        </div>
      </div>
    </div>
  );
};